import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Typography }  from '../Layout';
import InfoTooltip from '../InfoTooltip';
import { TitleH3, TitleSpan, gridItemStyle, checkboxStyle } from '../CreateDeviceContent/styles';
import i18n from '../../helpers/i18n';
import { Button, Collapse, Grid, IconButton, TextField, Tooltip } from '@mui/material';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import UploadFile from '@mui/icons-material/UploadFile';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';

const IP_COLLAPSE_THRESHOLD = 10;

function GeolocationFlag({ formik, controlsDisabled }) {
    return (
        <div style={checkboxStyle}>
            <Checkbox
                label={i18n.t('pages.newDevice.geolocation')}
                checked={formik.values.only_with_location}
                onChange={(event) => formik.setFieldValue('only_with_location', event.target.checked)}
                disabled={controlsDisabled}
            />
            <InfoTooltip tooltip={i18n.t('pages.newDevice.geolocationDescription')} />
        </div>
    );
}

function IpRangeFlag({ formik, controlsDisabled }) {
    const fileInputRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(true);
    const shouldCollapse = formik.values.ips.length > IP_COLLAPSE_THRESHOLD;

    useEffect(() => {
        if (formik.values.range_ip && formik.values.ips.length === 0) {
            formik.setFieldValue('ips', ['']);
        }
    }, [formik]);

    const handleAddIp = () => {
        formik.setFieldValue('ips', [...formik.values.ips, '']);
    };

    const handleRemoveIp = (index) => {
        const updatedIps = formik.values.ips.filter((_, i) => i !== index);
        formik.setFieldValue('ips', updatedIps);
    };

    const handleIpChange = (index, value) => {
        const updatedIps = [...formik.values.ips];
        updatedIps[index] = value.trim();
        formik.setFieldValue('ips', updatedIps);
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleImportFile = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            const importedIps = String(reader.result)
                .split(/\r\n|\n|\r|,|;/)
                .map((ip) => ip.trim())
                .filter((ip) => ip.length > 0);

            if (importedIps.length > 0) {
                const currentIps = formik.values.ips.filter((ip) => ip.trim().length > 0);
                formik.setFieldValue('range_ip', true);
                formik.setFieldValue('ips', [...currentIps, ...importedIps]);
            }
        };
        reader.readAsText(file);

        event.target.value = '';
    };

    return (
        <>
        <div style={checkboxStyle}>
            <Checkbox
                label={i18n.t('pages.newDevice.ipRange')}
                checked={formik.values.range_ip}
                onChange={(event) => {
                    const checked = event.target.checked;
                    formik.setFieldValue('range_ip', checked);
                    if (!checked) {
                        formik.setFieldValue('ips', []);
                    }
                }}
                disabled={controlsDisabled}
            />
            <InfoTooltip tooltip={i18n.t('pages.newDevice.ipRangeDescription')} />
            <Tooltip title={i18n.t('pages.newDevice.importIpRange')}>
                <span>
                    <IconButton onClick={handleImportClick} disabled={controlsDisabled || !formik.values.range_ip}>
                        <UploadFile />
                    </IconButton>
                </span>
            </Tooltip>
            <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleImportFile}
            />
        </div>

         {formik.values.range_ip && (
            <>
            {shouldCollapse && (
                <Button
                    color="primary"
                    onClick={() => setIsExpanded((prev) => !prev)}
                    style={{ paddingLeft: '40px', justifyContent: 'flex-start', textTransform: 'none' }}
                >
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    {i18n.t(isExpanded ? 'pages.newDevice.hideIpRanges' : 'pages.newDevice.showIpRanges', { count: formik.values.ips.length })}
                </Button>
            )}
            <Collapse in={!shouldCollapse || isExpanded}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', justifyContent: 'start', paddingLeft: '40px'}}>
                {formik.values.ips.map((ip, index) => (
                    <div
                        key={index}
                        style={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}
                    >
                        <TextField
                            id={`ip-range-${index}`}
                            label={`Faixa ${index + 1}`}
                            value={ip}
                            onChange={(event) => handleIpChange(index, event.target.value)}
                            disabled={controlsDisabled}
                            sx={{ width: '50%' }}
                        />
                        <Button
                            variant="link"
                            color="primary"
                            onClick={handleAddIp}
                            disabled={controlsDisabled}
                            style={{
                                borderRadius: '100%',
                                minWidth: '40px',
                                width: '40px',
                                height: '40px',
                                fontSize: '24px',
                                color: 'black',
                            }}
                        >
                            <Add />
                        </Button>
                        <Button
                            variant="link"
                            color="primary"
                            disabled={controlsDisabled}
                            onClick={() => handleRemoveIp(index)}
                            style={{
                                borderRadius: '100%',
                                minWidth: '40px',
                                width: '40px',
                                height: '40px',
                                fontSize: '16px',
                            }}
                        >
                            <Close />
                        </Button>
                    </div>
                ))}
            </div>
            </Collapse>
         </>)}
        </>
    );
}

function OnlineOnlyFlag({ formik, controlsDisabled }) {
    return (
        <div style={checkboxStyle}>
            <Checkbox
                label={i18n.t('pages.newDevice.networkConnection')}
                checked={formik.values.online_only}
                onChange={(event) => formik.setFieldValue('online_only', event.target.checked)}
                disabled={controlsDisabled}
            />
            <InfoTooltip tooltip={i18n.t('pages.newDevice.networkConnectionDescription')} />
        </div>
    );
}

function DateHourTimezoneAutoFlag({ formik, controlsDisabled }) {
    return (
        <div style={checkboxStyle}>
            <Checkbox
                label={i18n.t('pages.newDevice.dateHourTimezoneAuto')}
                checked={formik.values.auto_timezone}
                onChange={(event) => formik.setFieldValue('auto_timezone', event.target.checked)}
                disabled={controlsDisabled}
            />
            <InfoTooltip tooltip={i18n.t('pages.newDevice.dateHourTimezoneAutoDescription')} />
        </div>
    );
}

function OnlyInsideGeofence({ formik, controlsDisabled }) {
    return (
        <div style={checkboxStyle}>
            <Checkbox
                label={i18n.t('pages.newDevice.onlyInsideGeofence')}
                checked={formik.values.only_inside_geofence}
                onChange={(event) => formik.setFieldValue('only_inside_geofence', event.target.checked)}
                disabled={controlsDisabled}
            />
            <InfoTooltip tooltip={i18n.t('pages.newDevice.onlyInsideGeofenceDescription')} />
        </div>
    );
}


export default function RestrictionSection({ formik, deviceArchived, permissions }) {
    const controlsDisabled = deviceArchived || formik.values.device_for_receipt_flag_multi;

    const shouldRenderRestrictionSection =
        (formik.values.deviceType === 'ahgora_online_device' && (
            permissions?.batida_faixa_ip
        )) ||
        (formik.values.deviceType === 'ahgora_multi_device' && (
            true
        )) ||
        (formik.values.deviceType === 'biometry_device' && (
            true
        )) ||
        (formik.values.deviceType === 'my_ahgora_device' && (
            true
        ));

    if (!shouldRenderRestrictionSection) {
        return null;
    }

    return (
        <>
            {formik.values.deviceType === 'ahgora_online_device' && (
                <Grid style={gridItemStyle} item xs={12} md={6}>
                    <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
                        <TitleH3>
                            {i18n.t('pages.newDevice.restriction')} <TitleSpan>{i18n.t('pages.newDevice.optional')}</TitleSpan>
                        </TitleH3>
                    </div>
                    <Typography variant="body2">{i18n.t('pages.newDevice.restrictionSubtitle')}</Typography>
                    {permissions?.batida_faixa_ip && (
                        <IpRangeFlag formik={formik} controlsDisabled={controlsDisabled} />
                    )}
                </Grid>
            )}
            {['ahgora_multi_device', 'biometry_device'].includes(formik.values.deviceType) && (
                <Grid style={gridItemStyle} item xs={12} md={6}>
                    <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
                        <TitleH3>
                            {i18n.t('pages.newDevice.restriction')} <TitleSpan>{i18n.t('pages.newDevice.optional')}</TitleSpan>
                        </TitleH3>
                    </div>
                    <Typography variant="body2">{i18n.t('pages.newDevice.restrictionSubtitle')}</Typography>
                    <OnlineOnlyFlag formik={formik} controlsDisabled={controlsDisabled}/>
                    <DateHourTimezoneAutoFlag formik={formik} controlsDisabled={controlsDisabled} />
                    <GeolocationFlag formik={formik} controlsDisabled={controlsDisabled}/>
                    {permissions?.habilita_cerca_virtual && formik.values.only_with_location &&(
                        <OnlyInsideGeofence formik={formik} controlsDisabled={controlsDisabled} />
                    )}
                    {permissions?.batida_faixa_ip && (
                        <IpRangeFlag formik={formik} controlsDisabled={controlsDisabled}/>
                    )}
                </Grid>
            )}
            {formik.values.deviceType === 'my_ahgora_device' && (
                <Grid style={gridItemStyle} item xs={12} md={6}>
                    <div style={{ alignItems: 'center', display: 'flex', gap: '8px' }}>
                        <TitleH3>
                            {i18n.t('pages.newDevice.restriction')} <TitleSpan>{i18n.t('pages.newDevice.optional')}</TitleSpan>
                        </TitleH3>
                    </div>
                    <Typography variant="body2">{i18n.t('pages.newDevice.restrictionSubtitle')}</Typography>
                    <GeolocationFlag formik={formik} controlsDisabled={controlsDisabled}/>
                    {permissions?.habilita_cerca_virtual && formik.values.only_with_location &&(
                        <OnlyInsideGeofence formik={formik} controlsDisabled={controlsDisabled} />
                    )}
                    {permissions?.batida_faixa_ip && (
                        <IpRangeFlag formik={formik} controlsDisabled={controlsDisabled}/>
                    )}
                </Grid>
            )}
        </>
    );
}
